import { useMemo } from 'react';
import DashboardNavigationBar from '../../../components/dashboard/navigation-bar/dashboard-navigation-bar';
import DashboardLayout from '@/components/dashboard/dashboard-layout/dashboard-layout';
import DashboardGrid from '@/components/dashboard/dashboard-grid/dashboard-grid';
import Resources from '@/components/resources/resources';
import { useGetBiomarkersV1BiomarkersGet } from '@/api/generated-catch-backend/biomarkers/biomarkers';
import type { BiomarkerResult } from '../../../api/generated-catch-backend/model';
import { LabsHeaderAllBiomarkers } from './components/labs-headers';
import { BiomarkerItemCard } from './components/biomarker-dashboard/biomarker-item-card';
import { BiomarkerFilter } from './components/biomarker-filter/biomarker-filter';
import { useBiomarkerFilter } from './hooks/use-biomarker-filter';
import { FeatureFlags, useFeatureFlags } from '@/services/feature-flag-provider';
import '@/app/components/dashboard/dashboard-reset.css';

export function Page() {
  const { isEnabled } = useFeatureFlags();
  const isCatchLabsEnabled = isEnabled(FeatureFlags.IsCatchLabsEnabled);

  // Fetch biomarkers
  const { data: biomarkersData, isLoading: isLoadingBiomarkers, isError, error } = useGetBiomarkersV1BiomarkersGet();

  // Flatten and sort all biomarkers alphabetically
  const allBiomarkers = useMemo(() => {
    if (!biomarkersData || !biomarkersData.categories) {
      return [];
    }

    const flattened: BiomarkerResult[] = [];
    biomarkersData.categories.forEach((category) => {
      flattened.push(...category.biomarkers);
    });

    // Sort alphabetically by display name
    return flattened.sort((a, b) =>
      a.metadata.displayName.localeCompare(b.metadata.displayName)
    );
  }, [biomarkersData]);

  // Use shared biomarker filtering logic
  const { selectedFilter, setSelectedFilter, availableClassifications, filteredBiomarkers } =
    useBiomarkerFilter(allBiomarkers);


  if (!isCatchLabsEnabled) {
    return (
      <>
        <DashboardNavigationBar activeItem="labs" />
        <DashboardLayout>
          <DashboardGrid>
            <div className="rounded-lg bg-white p-8 text-center shadow-md">
              <div className="mb-6 text-6xl">
                <span role="img" aria-label="rocket">
                  🚀
                </span>
              </div>
              <h1 className="mb-4 text-3xl font-bold text-gray-900">This functionality is coming soon!</h1>
              <p className="text-gray-600">
                We're working hard to bring you something amazing.
                <br />
                Check back later for updates.
              </p>
            </div>
            <Resources as="aside" width="full" />
          </DashboardGrid>
        </DashboardLayout>
      </>
    );
  }

  return (
    <>
      <DashboardNavigationBar activeItem="labs" />

      {/* All Biomarkers Header */}
      <LabsHeaderAllBiomarkers />

      <DashboardLayout>
        <DashboardGrid>
          <div className="dashboard-reset py-6">
            <div className="mb-8 rounded-[10px] bg-[#F7F9FB] px-4 py-5 md:px-12 md:pb-10 md:pt-11">
              {isLoadingBiomarkers ? (
                <div className="flex items-center justify-center py-12">
                  <div className="text-center">
                    <div className="mb-4 inline-block h-8 w-8 animate-spin rounded-full border-4 border-solid border-blue-600 border-r-transparent"></div>
                    <p className="text-sm text-gray-600">Loading your biomarkers...</p>
                  </div>
                </div>
              ) : isError ? (
                <div className="rounded-lg bg-red-50 p-6 text-center">
                  <p className="mb-2 font-semibold text-red-900">Unable to load biomarkers</p>
                  <p className="text-sm text-red-700">{error instanceof Error ? error.message : 'An error occurred'}</p>
                </div>
              ) : allBiomarkers.length === 0 ? (
                <div className="rounded-lg bg-gray-50 p-6 text-center">
                  <p className="font-semibold text-gray-900">No biomarkers available yet</p>
                  <p className="text-sm text-gray-600">Your biomarkers will appear here once your results are ready.</p>
                </div>
              ) : (
                <>
                  {/* Filter dropdown */}
                  {availableClassifications.size > 0 && (
                    <div className="mb-6">
                      <BiomarkerFilter
                        availableClassifications={availableClassifications}
                        selectedFilter={selectedFilter}
                        onFilterChange={setSelectedFilter}
                      />
                    </div>
                  )}

                  {/* Biomarker list */}
                  <div className="flex flex-col gap-4">
                    {filteredBiomarkers.map((biomarker, index) => (
                      <BiomarkerItemCard key={`${biomarker.metadata.slug}-${index}`} biomarker={biomarker} />
                    ))}
                  </div>
                </>
              )}
            </div>
          </div>
          <Resources as="aside" width="full" />
        </DashboardGrid>
      </DashboardLayout>
    </>
  );
}
